import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProducts, getBrands } from './services/firebaseService';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import EngineeringIcon from '@mui/icons-material/Engineering';
import weldingHome from './assets/home.png';
// Fallback supplier logos (used when no brands are configured in Firestore)
import acoLogo from './assets/supplier/aco.png';
import rilandLogo from './assets/supplier/riland.png';
import karcherLogo from './assets/supplier/karcher.png';
import dongchengLogo from './assets/supplier/dongcheng.png';
import mostaqLogo from './assets/supplier/mostaq.png';
import maswLogo from './assets/supplier/masw.png';
import hyundaiLogo from './assets/supplier/hyundai.png';
import cayaLogo from './assets/supplier/caya.png';
import amaxxLogo from './assets/supplier/amaxx.png';
import ducarLogo from './assets/supplier/ducar.png';

function Home() {
  const { t } = useTranslation();
  const [showTitle, setShowTitle] = useState(false);
  const [showCollaborators, setShowCollaborators] = useState(false);
  const collaboratorsRef = useRef(null);
  const carouselRef = useRef(null);
  const [promotions, setPromotions] = useState([]);
  const [promotionsLoading, setPromotionsLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  // How many times the supplier list is repeated so the marquee always fills the
  // track and can scroll seamlessly (kept even so the two halves stay identical).
  const [repeatCount, setRepeatCount] = useState(2);

  // Fallback supplier logos shown until brands are loaded from Firestore
  const fallbackSuppliers = [
    { name: 'ACO', logo: acoLogo },
    { name: 'Riland', logo: rilandLogo },
    { name: 'Karcher', logo: karcherLogo },
    { name: 'Dongcheng', logo: dongchengLogo },
    { name: 'Mostaq', logo: mostaqLogo },
    { name: 'MASW', logo: maswLogo },
    { name: 'Hyundai', logo: hyundaiLogo },
    { name: 'Caya', logo: cayaLogo },
    { name: 'Amaxx', logo: amaxxLogo },
    { name: 'Ducar', logo: ducarLogo },
  ];

  // Use Firestore brands when available, otherwise fall back to bundled logos
  const suppliers = brands.length > 0 ? brands : fallbackSuppliers;

  useEffect(() => {
    setTimeout(() => setShowTitle(true), 200);

    const collaboratorsElement = collaboratorsRef.current;

    // Collaborators reveal animation
    const collabObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShowCollaborators(true);
      },
      { threshold: 0.2 }
    );
    if (collaboratorsElement) collabObs.observe(collaboratorsElement);

    return () => {
      collabObs.disconnect();
    };
  }, []);

  // Repeat the logos enough times so each half of the track is at least as wide
  // as the container, keeping the marquee seamless regardless of how many brands.
  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (!carouselElement || suppliers.length === 0) return;

    const measure = () => {
      const baseWidth = carouselElement.scrollWidth / repeatCount;
      if (baseWidth <= 0) return;
      const halfCopies = Math.max(1, Math.ceil(carouselElement.clientWidth / baseWidth));
      const desiredRepeat = halfCopies * 2;
      setRepeatCount((prev) => (prev === desiredRepeat ? prev : desiredRepeat));
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [suppliers.length, repeatCount]);

  // Auto-scroll carousel with seamless loop
  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (!carouselElement) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let animationFrameId = null;
    let lastTimestamp = 0;
    let isPaused = false;
    const speedPxPerSecond = 40;

    const animateCarousel = (timestamp) => {
      if (!lastTimestamp) {
        lastTimestamp = timestamp;
      }

      const deltaSeconds = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      if (!isPaused) {
        const loopWidth = carouselElement.scrollWidth / 2;
        carouselElement.scrollLeft += speedPxPerSecond * deltaSeconds;
        if (carouselElement.scrollLeft >= loopWidth) {
          carouselElement.scrollLeft -= loopWidth;
        }
      }

      animationFrameId = window.requestAnimationFrame(animateCarousel);
    };

    carouselElement.scrollLeft = 0;
    animationFrameId = window.requestAnimationFrame(animateCarousel);

    // Pause on hover and touch
    const handleMouseEnter = () => {
      isPaused = true;
    };
    const handleMouseLeave = () => {
      isPaused = false;
      lastTimestamp = 0;
    };
    const handleTouchStart = () => {
      isPaused = true;
    };
    const handleTouchEnd = () => {
      isPaused = false;
      lastTimestamp = 0;
    };

    carouselElement.addEventListener('mouseenter', handleMouseEnter);
    carouselElement.addEventListener('mouseleave', handleMouseLeave);
    carouselElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    carouselElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    carouselElement.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      carouselElement.removeEventListener('mouseenter', handleMouseEnter);
      carouselElement.removeEventListener('mouseleave', handleMouseLeave);
      carouselElement.removeEventListener('touchstart', handleTouchStart);
      carouselElement.removeEventListener('touchend', handleTouchEnd);
      carouselElement.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [repeatCount, suppliers.length]);

  useEffect(() => {
    let isMounted = true;

    const loadPromotions = async () => {
      setPromotionsLoading(true);
      const result = await getProducts();
      if (!isMounted) return;

      if (result.success) {
        const promoProducts = result.products
          .filter((product) => product.category === 'Others' && product.status !== 'disable')
          .slice(0, 6);
        setPromotions(promoProducts);
      } else {
        setPromotions([]);
      }

      setPromotionsLoading(false);
    };

    loadPromotions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadBrands = async () => {
      const result = await getBrands();
      if (!isMounted) return;

      if (result.success && result.brands.length > 0) {
        setBrands(
          result.brands.map((brand) => ({
            name: brand.brandName,
            logo: brand.brandLogo
          }))
        );
      }
    };

    loadBrands();
    return () => {
      isMounted = false;
    };
  }, []);

  const parsePrice = (price) => {
    if (price === '' || price == null) return NaN;
    const numPrice = parseFloat(price.toString().replace(/[^\d.]/g, ''));
    return Number.isNaN(numPrice) ? NaN : numPrice;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    const numPrice = parsePrice(price);
    if (Number.isNaN(numPrice)) return price;
    return `RM ${numPrice.toFixed(2)}`;
  };

  const getDiscountPercent = (normalPrice, sellingPrice) => {
    const normal = parsePrice(normalPrice);
    const selling = parsePrice(sellingPrice);
    if (Number.isNaN(normal) || Number.isNaN(selling) || normal <= 0 || normal <= selling) {
      return 0;
    }
    return Math.round(((normal - selling) / normal) * 100);
  };

  const hasNormalPrice = (normalPrice) =>
    normalPrice !== undefined && normalPrice !== null && String(normalPrice).trim() !== '';

  const categoryTranslationKeys = {
    'Welding Machines': 'products.weldingMachines',
    Accessories: 'products.accessories',
    'Power Tools': 'products.powerTools',
    Generator: 'products.generator',
    'Air Compressors': 'products.airCompressors',
    'Home Improvements': 'products.homeImprovements',
    Others: 'products.promotions',
    Promotion: 'products.promotions',
    'Machinery Service Consultant': 'products.machineryService'
  };

  const getCardLabel = (product) => {
    const label = (product.subCategory || product.category || 'Promotion').toString();
    const translationKey = categoryTranslationKeys[label];
    return translationKey ? t(translationKey) : label;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative w-full overflow-hidden border-b border-gray-200 bg-gray-100">
        <img
          src={weldingHome}
          alt={t('home.heroImageAlt')}
          className="absolute inset-0 h-full w-full object-cover opacity-90 mix-blend-multiply md:static md:h-auto md:object-contain"
        />
        <div className="relative z-10 mx-auto flex min-h-[60vh] w-full max-w-7xl items-center px-4 py-12 md:absolute md:inset-0 md:min-h-0 md:py-20 md:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white/75 px-3 py-1">
              <svg className="h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">{t('home.trustedSince')}</span>
            </div>
            <h1
              className={`mb-6 transform text-3xl font-bold tracking-tight text-primary transition-all duration-700 sm:text-4xl md:text-6xl ${
                showTitle ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0'
              }`}
            >
              {t('home.title')}
            </h1>
            <p className="mb-8 max-w-2xl text-lg text-gray-300 md:text-2xl">
              {t('home.subtitle')}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/products"
                className="inline-flex items-center justify-center rounded-md bg-primary px-8 py-3 font-semibold text-white shadow-md transition-colors hover:bg-blue-900"
              >
                {t('home.browseCatalog')}
                <span className="ml-2 text-lg">→</span>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center rounded-md border-2 border-gray-300 px-8 py-3 font-semibold text-gray-300 transition-colors hover:bg-primary/10"
              >
                {t('home.viewCapabilities')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Collaborators Section */}
      <section ref={collaboratorsRef} className="border-b border-gray-200 bg-white py-14 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="mb-8 text-center text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">
            {t('home.brandPartners')}
          </p>
          <div
            ref={carouselRef}
            className="flex items-center gap-12 overflow-hidden whitespace-nowrap"
          >
            {/* Repeat logos to fill the track for a seamless auto-scroll marquee */}
            {Array.from({ length: repeatCount }, () => suppliers).flat().map((supplier, idx) => (
              <img
                key={`${supplier.name}-${idx}`}
                src={supplier.logo}
                alt={supplier.name}
                className={`w-16 h-16 md:w-20 md:h-20 object-contain shrink-0 transition-all duration-700
                  ${showCollaborators ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
                  hover:scale-105
                `}
                style={{ transitionDelay: `${0.1 + idx * 0.1}s` }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Promotions Section */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between border-b-2 border-gray-200 pb-4">
            <h2 className="text-3xl font-bold tracking-tight text-primary md:text-4xl">{t('home.latestPromotions')}</h2>
            <Link to="/products" className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline underline-offset-4">
              {t('home.viewAllOffers')} <span className="text-base">→</span>
            </Link>
          </div>

          {promotionsLoading ? (
            <div className="py-10 text-center text-gray-500">{t('home.loadingPromotions')}</div>
          ) : (
            <div className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-px-4 pb-2 [-webkit-overflow-scrolling:touch]">
              {promotions.map((product) => {
                const showNormalPrice = hasNormalPrice(product.normalPrice);
                const discountPercent = getDiscountPercent(product.normalPrice, product.price);
                return (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group flex h-[28rem] w-72 shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:shadow-lg sm:w-80 lg:w-[calc((100%-4.5rem)/4)]"
                >
                  <div className="relative flex h-52 items-center justify-center overflow-hidden border-b border-gray-200 bg-gray-100 p-4">
                    {discountPercent > 0 && (
                      <span className="absolute left-3 top-3 z-10 rounded bg-red-500 px-2 py-1 text-xs font-bold text-white shadow-sm">
                        -{discountPercent}%
                      </span>
                    )}
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full object-contain mix-blend-darken transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex h-[14rem] flex-col p-4">
                    <div className="h-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {getCardLabel(product)}
                    </div>
                    <h3 className="mb-3 mt-2 h-22 overflow-hidden text-base font-bold leading-6 text-primary">{product.name}</h3>
                    <div className="mt-auto">
                      <div className="mb-4 min-h-[2rem]">
                        {showNormalPrice && (
                          <div className="text-sm text-gray-500 line-through">
                            {formatPrice(product.normalPrice)}
                          </div>
                        )}
                        <div className="text-2xl font-bold leading-8 text-primary">
                          {formatPrice(product.price)}
                        </div>
                      </div>
                      <span className="flex h-10 w-full items-center justify-center gap-2 rounded-md border-2 border-primary px-4 font-semibold text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                        {t('home.shopNow')}
                      </span>
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Expert Repair & Services Section */}
      <section className="relative overflow-hidden border-y-2 border-gray-200 bg-gray-100 py-16 px-4">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(#191c1e 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="mb-10 max-w-3xl">
            <h2 className="mb-3 text-3xl font-bold tracking-tight text-primary md:text-4xl">
              {t('home.expertRepairTitle')}
            </h2>
            <p className="text-lg text-gray-700">
              {t('home.expertRepairDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-gray-300 bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-gray-300 bg-gray-100">
                <BuildCircleIcon className="!h-9 !w-9 text-primary" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-primary">{t('home.routineMaintenanceTitle')}</h3>
              <p className="mb-4 text-gray-700">
                {t('home.routineMaintenanceDescription')}
              </p>
              <Link to="/contact" className="inline-flex items-center gap-1 font-semibold text-primary transition-colors hover:text-blue-700">
                {t('home.learnMore')} <span className="text-sm">→</span>
              </Link>
            </div>

            <div className="rounded-xl border border-gray-300 bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-gray-300 bg-gray-100">
                <EngineeringIcon className="!h-9 !w-9 text-primary" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-primary">{t('home.inHouseRepairsTitle')}</h3>
              <p className="mb-4 text-gray-700">
                {t('home.inHouseRepairsDescription')}
              </p>
              <Link to="/contact" className="inline-flex items-center gap-1 font-semibold text-primary transition-colors hover:text-blue-700">
                {t('home.learnMore')} <span className="text-sm">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>



    </div>
  );
}

export default Home;
